import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as authService from '../services/authService';

/**
 * Email Verification Required Page
 *
 * Shown after registration or when an unverified user tries to log in.
 * Provides a resend verification email button.
 */

export function EmailVerificationRequired(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  const handleResend = async () => {
    if (!email) return;

    setIsResending(true);
    setResendStatus('idle');
    setResendMessage('');

    try {
      const response = await authService.resendVerification(email);
      setResendStatus('success');
      setResendMessage(response.message);
    } catch (error: any) {
      setResendStatus('error');
      setResendMessage(error.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-blue-50 to-pink-100">
      {/* Navigation Header */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo1.png" alt="Nivoon" className="h-16" />
            </Link>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-md hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Nivoon
            </h1>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verify Your Email
            </h2>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center">
              {/* Email Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100">
                <svg
                  className="h-10 w-10 text-indigo-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <h3 className="mt-6 text-xl font-bold text-gray-900">
                Check Your Inbox
              </h3>

              <p className="mt-3 text-gray-600">
                We've sent a verification link to:
              </p>
              {email && (
                <p className="mt-1 font-semibold text-indigo-600">{email}</p>
              )}
              <p className="mt-3 text-sm text-gray-500">
                Click the link in the email to verify your account and start using Nivoon.
              </p>

              {/* Resend Status Messages */}
              {resendStatus === 'success' && (
                <div className="mt-4 rounded-md bg-green-50 p-3">
                  <p className="text-sm text-green-700">{resendMessage}</p>
                </div>
              )}
              {resendStatus === 'error' && (
                <div className="mt-4 rounded-md bg-red-50 p-3">
                  <p className="text-sm text-red-700">{resendMessage}</p>
                </div>
              )}

              {/* Resend Button */}
              <div className="mt-6 space-y-3">
                <p className="text-sm text-gray-500">
                  Didn't receive the email?
                </p>
                <button
                  onClick={handleResend}
                  disabled={isResending || !email}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>

                <Link
                  to="/login"
                  className="block w-full text-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-600">
            Need help? Contact support at{' '}
            <a href="mailto:support@nivoon.com" className="font-medium text-indigo-600 hover:text-indigo-500">
              support@nivoon.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationRequired;
